// What the cat decides to do next. Needs (energy, boredom, annoyance) change with what it is doing; when a step ends, the next
// one is drawn Markov-style from the current pose's options, each weighted by the needs and dropped if the space doesn't allow it
// (platform length, a wall to climb, a platform in jumping range). The settled poses (sit, lie, curl, back) mostly keep looping:
// each loop end leaves with probability 1 - exp(-loop length / expected stay), the stay itself set by the needs.
// Plans are built on a "virtual cat" so every step's travel is checked against the platform before anything plays.
import { AirStep, ClimbStep, ArriveStep, ClipStep, CycleStep, FallStep, HoldStep, TurnStep } from './steps.js';
import { ARRIVE_FORWARD, RUN_CYCLE_TRAVEL, STABLE, WALK_SPEED } from './rig.js';
import { platformBelow } from './world.js';
import { CLIMB_REACH, JUMP_DOWN, WALK_MIN, needDelta, JUMP_FAIL_EXTRA, JUMP_MAX, JUMP_UP, NEEDS, SHORT_FALL, SMALL_JUMP, WANDER_ENDS, choiceWeights, loopWeights, stayTime } from './policy.js';

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
function pick(options) {
  const live = options.filter((o) => o && o[1] > 0);
  const total = live.reduce((s, o) => s + o[1], 0);
  let r = Math.random() * total;
  for (const o of live) if ((r -= o[1]) <= 0) return o[0];
  return live.at(-1)?.[0] ?? null;
}

// Poses the cat can switch between where it stands (no walking)
const IN_PLACE = new Set(['stand', 'sit', 'lie', 'curl', 'back', 'stand_stare']);
const MARGIN = 0.25; // H kept from a platform's ends
const FRONT = 0.65; // H kept between the paws and the end of the platform it walks / runs towards (the head reaches ~0.55 H ahead)
const WALK_SCALE = [0.8, 1.25]; // how much a walk cycle's stride may be shortened / lengthened to end on target

export class Brain {
  constructor(rig) {
    this.rig = rig;
    this.H = rig.H;
    this.needs = { energy: 75, boredom: 40, annoyance: 0 };
    this.lastLoop = 4;
    this.intent = '';
    this.failed = {}; // plans that didn't fit, by choice (for the debug view / simulation)
  }

  tick(tag, dt) {
    for (const k of ['energy', 'boredom', 'annoyance']) this.needs[k] = clamp(this.needs[k] + needDelta(tag, k) * dt, 0, 100);
    this.needs.annoyance = clamp(this.needs.annoyance - 0.03 * dt, 0, 100);
  }

  /** Expected stay in a settled pose (s). */
  stay(pose) {
    return stayTime(pose, this.needs);
  }

  /** Steps for what comes next, from the cat's current pose and place. */
  next(cat, world) {
    this.intent = '';
    const plat = cat.surface;
    const v = { x: cat.x, dir: cat.dir, pose: cat.pose, plat };
    if (cat.pose === 'hang') return this.drop(cat);
    if (!plat) return [this.fall()];
    if (STABLE.has(cat.pose)) {
      const leave = 1 - Math.exp(-this.lastLoop / this.stay(cat.pose));
      if (Math.random() >= leave) return this.loop(cat.pose);
      this.intent = `离开 ${cat.pose}`;
      if (cat.pose === 'curl') return this.plan(v, (s) => this.clip(s, v, 'curl__wakeup__sit', 'rest'));
      if (cat.pose === 'back') return this.plan(v, (s) => this.clip(s, v, 'back__roll__sit', 'rest'));
      if (cat.pose === 'lie') return this.plan(v, (s) => this.clip(s, v, 'lie__start__stand', 'rest'));
      // sit
      return this.chooseFrom(choiceWeights('sit', this.needs), v, world) ?? this.loop('sit');
    }
    if (cat.pose === 'stand') {
      return this.chooseFrom(choiceWeights('stand', this.needs), v, world) ?? this.loop('stand');
    }
    if (cat.pose === 'stand_stare') {
      return this.chooseFrom(choiceWeights('stand_stare', this.needs), v, world) ?? this.plan(v, (s) => this.clip(s, v, 'stand_stare__stand__stand', 'idle'));
    }
    // Anything else (should not end a step here): get back to the ground in the simplest way
    if (cat.pose === 'walk') return this.plan(v, (s) => this.clip(s, v, 'walk__stop__stand', 'walk'));
    if (cat.pose === 'run') return this.plan(v, (s) => this.clip(s, v, 'run__stop__stand', 'run'));
    if (cat.pose === 'crouch') return this.plan(v, (s) => (s.push(new AirStep(this.rig.get('crouch__small_jump__stand_stare'), { target: { x: v.x + v.dir * 0.6 * this.H, y: plat.y } })), true));
    return [this.fall()];
  }

  /** Draw a choice by weight; if it doesn't fit here (no wall to climb, no room to run...), draw again from the others. */
  chooseFrom(options, v, world) {
    let left = options.filter((o) => o[1] > 0);
    while (left.length) {
      const choice = pick(left);
      const steps = this.choose(choice, { ...v }, world);
      if (steps) return steps;
      left = left.filter((o) => o[0] !== choice);
    }
    return null;
  }

  choose(choice, v, world) {
    this.intent = choice ?? '';
    const steps = this.build(choice, v, world);
    if (!steps) this.failed[choice] = (this.failed[choice] ?? 0) + 1;
    return steps;
  }

  build(choice, v, world) {
    switch (choice) {
      case 'idle':
        return this.loop(v.pose);
      case 'stand':
        return this.plan(v, (s) => this.bridge(s, v, 'stand'));
      case 'sit':
        return this.plan(v, (s) => this.bridge(s, v, 'sit'));
      case 'lie':
        return this.plan(v, (s) => this.bridge(s, v, 'lie'));
      case 'curl':
        return this.plan(v, (s) => this.bridge(s, v, 'curl'));
      case 'back':
        return this.plan(v, (s) => this.bridge(s, v, 'back'));
      case 'stare':
        return this.plan(v, (s) => this.bridge(s, v, 'stand_stare'));
      case 'wander':
        return this.wander(v);
      case 'run':
        return this.run(v);
      case 'jump':
        return this.jump(v, world);
      case 'climb':
        return this.climb(v, world);
    }
    return null;
  }

  // ---------------- loops in a pose ----------------

  loop(pose) {
    const r = this.rig;
    const tag = pose === 'curl' ? 'sleep' : pose === 'stand' ? 'idle' : 'rest';
    const anyFrom = r.from(pose)[0];
    const options = loopWeights(pose, this.needs).map(([o, w]) => [typeof o === 'string' ? r.get(o) : { hold: rand(o.hold[0], o.hold[1]) }, w]);
    const choice = pick(options.filter((o) => o[0]));
    if (choice?.hold !== undefined) {
      this.lastLoop = choice.hold;
      return [new HoldStep(anyFrom, choice.hold, tag)];
    }
    this.lastLoop = choice.duration;
    return [new ClipStep(choice, { tag })];
  }

  // ---------------- plans ----------------

  /** Build steps on a virtual cat; null if any step would leave the platform. */
  plan(v, build) {
    const steps = [];
    return build(steps) === false ? null : steps;
  }

  inside(v, x = v.x, margin = MARGIN) {
    // No margin (in-place changes): as far out as the cat may stand before it falls off (its platform may have shrunk under it)
    const m = margin ? margin * this.H : -0.15 * this.H;
    return x >= v.plat.x0 + m && x <= v.plat.x1 - m;
  }

  clip(steps, v, name, tag, opts = {}) {
    const c = this.rig.get(name);
    if (!c) return false;
    const travel = c.travel * (opts.scale ?? 1);
    const x = v.x + v.dir * travel;
    // In-place pose changes only need to stay on the platform; moves keep clear of its ends
    if (!this.inside(v, x, Math.abs(travel) < 0.3 * this.H ? 0 : MARGIN)) return false;
    steps.push(new ClipStep(c, { tag, ...opts }));
    v.x = x;
    v.pose = c.to;
    return true;
  }

  /** In-place pose changes, shortest path (only through poses the cat can hold where it stands). */
  bridge(steps, v, target) {
    if (v.pose === target) return true;
    const prev = new Map([[v.pose, null]]);
    const queue = [v.pose];
    while (queue.length) {
      const p = queue.shift();
      for (const c of this.rig.from(p)) {
        if (c.to === p || prev.has(c.to) || !IN_PLACE.has(c.to) || c.kind !== 'transition' || /jump|fall|run|climb/.test(c.name)) continue;
        prev.set(c.to, c);
        queue.push(c.to);
      }
    }
    if (!prev.has(target)) return false;
    const path = [];
    for (let p = target; prev.get(p); p = prev.get(p).from) path.unshift(prev.get(p));
    for (const c of path) if (!this.clip(steps, v, c.name, 'rest')) return false;
    return true;
  }

  /** Face a direction: turning around only happens sitting. */
  face(steps, v, dir) {
    if (v.dir === dir) return true;
    if (!this.bridge(steps, v, 'sit')) return false;
    steps.push(new TurnStep());
    v.dir = dir;
    return true;
  }

  /**
   * Get to x and stop in `end` (stand / sit / stand_stare). Short moves (under WALK_MIN) are a hop; longer ones a walk, which starts
   * from sitting and always plays at least one full cycle at 0.8–1.25 of its stride (never walking on the spot).
   */
  walkTo(steps, v, x, end = 'stand', { edge = false } = {}) {
    const H = this.H;
    const D = Math.abs(x - v.x);
    if (D < 0.3 * H) return this.bridge(steps, v, end);
    // The whole body stops short of the end it walks towards (the head is well ahead of the paws), unless it is meant to go
    // right up to the edge (to jump off it or climb the wall there)
    const ahead = (edge ? MARGIN : FRONT) * H;
    const toward = Math.sign(x - v.x);
    if (x < v.plat.x0 + (toward < 0 ? ahead : MARGIN * H) || x > v.plat.x1 - (toward > 0 ? ahead : MARGIN * H)) return false;
    if (D < WALK_MIN * H) return this.hopTo(steps, v, x, end);
    const r = this.rig;
    const start = r.get('sit__start__walk');
    const happy = this.needs.energy > 60 && this.needs.annoyance < 20 && Math.random() < 0.4;
    const cycle = r.get(happy ? 'walk__walk_satisfy__walk' : 'walk__walk__walk');
    if (!start || !cycle) return false;
    // The stop that ends in the wanted pose if it fits, else stopping standing and changing pose after
    const stops = [{ stand: 'walk__stop__stand', sit: 'walk__stop__sit', stand_stare: 'walk__stop__stand_stare' }[end], 'walk__stop__stand'].map((n) => r.get(n)).filter(Boolean);
    for (const stop of stops) {
      const rem = D - start.travel - stop.travel;
      const ideal = rem / cycle.travel;
      const lo = Math.max(1, Math.ceil(ideal / WALK_SCALE[1]));
      const hi = Math.floor(ideal / WALK_SCALE[0]);
      if (hi < lo) continue;
      const n = clamp(Math.round(ideal), lo, hi);
      const dir = Math.sign(x - v.x);
      if (!this.bridge(steps, v, 'sit') || !this.face(steps, v, dir)) return false;
      steps.push(new ClipStep(start, { tag: 'walk' }));
      // The walk in the clips is slow (short strides): cycles play faster, frames and travel together so the paws don't slide
      steps.push(new CycleStep(cycle, n, { tag: 'walk', scale: rem / (n * cycle.travel), speed: WALK_SPEED }));
      steps.push(new ClipStep(stop, { tag: 'walk' }));
      v.x = x;
      v.pose = stop.to;
      return end === stop.to ? true : this.bridge(steps, v, end);
    }
    return D <= JUMP_MAX * H ? this.hopTo(steps, v, x, end) : false;
  }

  /** A hop to x on the same platform (for short moves): a small jump, or a normal one beyond its reach. */
  hopTo(steps, v, x, end) {
    const H = this.H;
    const r = this.rig;
    const crouch = r.get('stand_stare__start__crouch');
    if (!crouch) return false;
    const dir = Math.sign(x - v.x);
    if (!this.bridge(steps, v, 'stand_stare')) return false;
    if (v.dir !== dir && !(this.face(steps, v, dir) && this.bridge(steps, v, 'stand_stare'))) return false;
    if (!this.clip(steps, v, crouch.name, 'jump')) return false;
    const left = Math.abs(x - v.x);
    const clip = r.get(left < SMALL_JUMP.dx * H ? 'crouch__small_jump__stand_stare' : 'crouch__jump__stand_stare');
    if (!clip?.air || left > JUMP_MAX * H) return false;
    steps.push(new AirStep(clip, { target: { x, y: v.plat.y }, tag: 'jump' }));
    // After landing the clip still moves a little
    const after = clip.dx.slice(clip.air[1] + 1, clip.frames).reduce((a, b) => a + b, 0);
    v.x = x + v.dir * after;
    v.pose = clip.to;
    return this.inside(v, v.x, 0) && this.bridge(steps, v, end);
  }

  wander(v) {
    const H = this.H;
    const room = { left: v.x - v.plat.x0 - FRONT * H, right: v.plat.x1 - FRONT * H - v.x };
    const dir = room.right > room.left ? (Math.random() < 0.75 ? 1 : -1) : Math.random() < 0.75 ? -1 : 1;
    const dist = Math.min(room[dir > 0 ? 'right' : 'left'], rand(1.5, 8) * H);
    if (dist < 1.2 * H) return null;
    const end = pick(WANDER_ENDS);
    this.intent = `走走 → ${end}`;
    return this.plan(v, (s) => this.walkTo(s, v, v.x + dir * dist, end));
  }

  run(v) {
    const H = this.H;
    const r = this.rig;
    const room = { left: v.x - v.plat.x0 - FRONT * H, right: v.plat.x1 - FRONT * H - v.x };
    const dir = room.right > room.left ? 1 : -1;
    const start = r.get('stand_stare__start__run');
    const cycle = r.get('run__run__run');
    const stop = r.get('run__stop__stand');
    if (!start || !cycle || !stop) return null;
    const need = start.travel + stop.travel + RUN_CYCLE_TRAVEL * H * 2;
    const D = Math.min(room[dir > 0 ? 'right' : 'left'], rand(5, 12) * H);
    if (D < need) return null;
    this.intent = '跑一段';
    return this.plan(v, (s) => {
      if (v.dir !== dir && !(this.face(s, v, dir) && this.bridge(s, v, 'stand_stare'))) return false;
      if (!this.bridge(s, v, 'stand_stare')) return false;
      const n = Math.max(2, Math.round((D - start.travel - stop.travel) / cycle.travel));
      const scale = (D - start.travel - stop.travel) / (n * cycle.travel);
      s.push(new ClipStep(start, { tag: 'run' }), new CycleStep(cycle, n, { tag: 'run', scale }), new ClipStep(stop, { tag: 'run' }));
      v.x += dir * D;
      v.pose = stop.to;
      return true;
    });
  }

  /** Jump to another platform (or try and miss it). */
  jump(v, world) {
    const H = this.H;
    const P = v.plat;
    const m = MARGIN * H;
    const options = [];
    // A perch (a desktop icon: narrower than the usual landing room): onto its middle, and off it from its middle
    const narrow = (p) => p.x1 - p.x0 < 1.6 * H;
    for (const Q of world.platforms) {
      if (Q.id === P.id) continue;
      const dy = Q.y - P.y; // < 0: up
      for (const d of [1, -1]) {
        let launch, land;
        // Across a gap: land well clear of the far edge (further when jumping down)
        const inset = dy > 0.5 * H ? 1.2 * H : 0.8 * H;
        const mid = (Q.x0 + Q.x1) / 2;
        if (d > 0 && Q.x0 >= P.x1 - 0.5 * H) (launch = narrow(P) ? (P.x0 + P.x1) / 2 : P.x1 - 0.4 * H), (land = narrow(Q) ? mid : Q.x0 + inset);
        else if (d < 0 && Q.x1 <= P.x0 + 0.5 * H) (launch = narrow(P) ? (P.x0 + P.x1) / 2 : P.x0 + 0.4 * H), (land = narrow(Q) ? mid : Q.x1 - inset);
        else if (narrow(Q) && dy < -0.5 * H) {
          // Up onto a perch over it: from 1.3 H to the side of its middle
          land = mid;
          launch = clamp(land - d * 1.3 * H, P.x0 + m, P.x1 - m);
        } else {
          // Above, overlapping: jump up onto it from 1.3 H before (below and overlapping would mean dropping through the
          // window it stands on: it jumps off an end instead, handled above)
          if (dy > 0) continue;
          const lo = Math.max(Q.x0 + 0.6 * H, P.x0 + m + (d > 0 ? 1.3 * H : 0));
          const hi = Math.min(Q.x1 - 0.6 * H, P.x1 - m - (d < 0 ? 1.3 * H : 0));
          if (hi < lo || Math.abs(dy) < 0.5 * H) continue;
          land = clamp(v.x + d * 1.3 * H, lo, hi);
          launch = land - d * 1.3 * H;
        }
        if (launch < P.x0 + m || launch > P.x1 - m || land < Q.x0 || land > Q.x1) continue;
        const dx = Math.abs(land - launch);
        const ok = dx <= JUMP_MAX * H && -dy <= JUMP_UP * H && dy <= JUMP_DOWN * H;
        const tryIt = !ok && dx <= (JUMP_MAX + JUMP_FAIL_EXTRA) * H && -dy <= (JUMP_UP + 0.8) * H && dy <= JUMP_DOWN * H;
        // Other windows are what it wants; the floor is just the way down
        const kind = Q.keys.every((k) => k.startsWith('floor:')) ? 0.5 : Q.keys.some((k) => P.keys.some((p) => p.split(':')[0] === k.split(':')[0])) ? 1 : 3;
        if (ok || tryIt) options.push([{ Q, d, launch, land, ok, dx, dy }, (kind * (ok ? 1 : 0.25)) / (1 + Math.abs(launch - v.x) / (6 * H))]);
      }
    }
    const o = pick(options);
    if (!o) return null;
    const r = this.rig;
    const crouch = r.get('stand_stare__start__crouch');
    // High jumps don't always make it: from 3 H up the chance of missing grows to 45% at the limit
    const rise = Math.max(0, -o.dy) / H;
    if (o.ok && Math.random() < 0.45 * Math.min(1, Math.max(0, (rise - 3) / (JUMP_UP - 3)))) o.ok = false;
    const small = o.ok && o.dx < SMALL_JUMP.dx * H && Math.abs(o.dy) < SMALL_JUMP.dy * H;
    const clip = r.get(o.ok ? (small ? 'crouch__small_jump__stand_stare' : 'crouch__jump__stand_stare') : 'crouch__jump_fail_fall__sit');
    if (!crouch || !clip) return null;
    this.intent = o.ok ? `跳到 (${Math.round(o.land)}, ${Math.round(o.Q.y)})` : '跳（够不着）';
    return this.plan(v, (s) => {
      if (!this.walkTo(s, v, o.launch - o.d * crouch.travel, 'stand_stare', { edge: true })) return false;
      if (v.dir !== o.d && !(this.face(s, v, o.d) && this.bridge(s, v, 'stand_stare'))) return false;
      if (!this.clip(s, v, crouch.name, 'jump')) return false;
      // A miss falls short: peaks just before the target's edge and below it, and comes down wherever it falls (often back
      // where it started); one out of range aims as far as it can
      const edge = o.d > 0 ? Math.max(o.launch + 0.6 * H, Math.min(o.land, o.Q.x0) - 0.3 * H) : Math.min(o.launch - 0.6 * H, Math.max(o.land, o.Q.x1) + 0.3 * H);
      const reach = o.launch + o.d * Math.min(Math.abs(edge - o.launch), JUMP_MAX * 0.95 * H);
      const target = o.ok ? { x: o.land, y: o.Q.y, plat: o.Q } : { x: reach, y: Math.max(o.Q.y + 0.6 * H, v.plat.y - JUMP_UP * 0.8 * H) };
      s.push(new AirStep(clip, { target, tag: 'jump' }));
      return true;
    });
  }

  /** Climb a window side: up and over if there is a ledge at the top, otherwise up a bit and fall back down. */
  climb(v, world) {
    const H = this.H;
    const P = v.plat;
    const r = this.rig;
    const start = r.get('stand_stare__start__climb');
    const cycle = r.get('climb__climb__climb');
    const arrive = r.get('climb__arrive__stand');
    if (!start || !cycle) return null;
    const options = [];
    for (const w of world.walls) {
      if (w.y1 < P.y - CLIMB_REACH * H || w.y0 > P.y - 1.6 * H || w.x < P.x0 || w.x > P.x1) continue;
      const d = w.side === 'left' ? 1 : -1; // facing the wall
      const at = w.x - d * start.travel;
      if (at < P.x0 + MARGIN * H || at > P.x1 - MARGIN * H) continue;
      const topOk = Math.abs(w.y0 - w.top) <= 2;
      // The ledge at the top: a window's top edge, or (between displays) the higher display's floor a little above the margin's end
      const top = topOk ? world.platforms.find((p) => p.y <= w.top + 4 && p.y >= w.top - 0.6 * H && w.x + d * (ARRIVE_FORWARD + 0.2) * H >= p.x0 && w.x + d * (ARRIVE_FORWARD + 0.2) * H <= p.x1) : null;
      options.push([{ w, d, at, top }, (top ? 1 : 0.6) / (1 + Math.abs(at - v.x) / (6 * H))]);
    }
    const o = pick(options);
    if (!o) return null;
    this.intent = o.top ? '爬上窗口' : '爬墙（上面没地方站）';
    return this.plan(v, (s) => {
      if (!this.walkTo(s, v, o.at, 'stand_stare', { edge: true })) return false;
      if (v.dir !== o.d && !(this.face(s, v, o.d) && this.bridge(s, v, 'stand_stare'))) return false;
      // On the wall now, not on the platform (so a window change meanwhile doesn't put it back down there)
      s.push(new FnStep((cat) => ((cat.wall = o.w), (cat.surface = null))));
      s.push(new ClipStep(start, { tag: 'climb' }));
      const startTop = P.y + (start.rootY.at(-1) - start.rootY[0]) * start.s; // root height when on the wall
      if (o.top && arrive) {
        s.push(new ClimbStep(cycle, Math.min(startTop, o.top.y + 0.35 * H)), new ArriveStep(arrive, o.top));
      } else {
        // Up a share of what there is of the wall (30-95%, more often high), then it lets go and falls
        const room = Math.max(0, startTop - (o.w.y0 + 0.5 * H));
        const highest = startTop - room * (0.3 + 0.65 * Math.sqrt(Math.random()));
        s.push(new ClimbStep(cycle, Math.min(startTop, highest)));
        s.push(new FnStep((cat, ctx) => ctx.queueFront(this.wallFall(cat, ctx.world))));
      }
      return true;
    });
  }

  /** Let go of a wall: a short fall clip for small heights, the long flailing one otherwise. */
  wallFall(cat, world) {
    const H = this.H;
    const name = platformBelow(world, cat.x - cat.dir * 0.5 * H, cat.y).y - cat.y < SHORT_FALL * H ? 'climb__short_fall__sit' : 'climb__fall__sit';
    return new AirStep(this.rig.get(name) ?? this.rig.get('climb__fall__sit'), { velocity: [-cat.dir * 1.0 * H, 0], tag: 'jump' });
  }

  /** Dropped by the user: falls from the pointer with a little of its swing, lands standing or sitting. */
  drop(cat) {
    const name = this.needs.annoyance > 30 || Math.random() < 0.5 ? 'hang__drop_land__stand' : 'hang__drop__sit';
    const vel = cat.throwVelocity ?? [0, 0];
    // Falls the moment it is let go: the held frames before the air phase are skipped
    return [new AirStep(this.rig.get(name), { velocity: vel, fromAir: true, tag: 'jump' })];
  }

  /** The floor went away (window closed or moved, or it walked off an edge): straight into falling, looped for a long drop. */
  fall(opts) {
    const loop = this.rig.get('fall__fall__fall');
    const land = this.rig.get('fall__fall__sit');
    if (loop && land) return new FallStep(loop, land, opts);
    return new AirStep(this.rig.get('climb__fall__sit'), { velocity: [0, 0], fromAir: true, tag: 'jump' });
  }
}


/** Runs a function instantly (set where the cat is climbing, queue what happens at the top). */
export class FnStep {
  tag = 'climb';
  constructor(fn) {
    this.fn = fn;
  }
  start(cat, ctx) {
    this.fn(cat, ctx);
  }
  update() {
    return true;
  }
}
